// src/components/CartOptimizerPanel.js
//
// Cart preferences: which copy of each card the cart buys. (TKT-2026-105225)
//
// A store can hold the same card as NM Normal, LP Normal, NM Foil and so on, at
// different prices. Until now the kiosk picked the first of those in a fixed
// order and the shopper had to change it card by card. This panel lets them say
// it once, for the whole cart.
//
// Three things about the shape of this control are deliberate:
//
//  * It is not shown at all unless the store turned it on, and each of the three
//    axes appears only if the store allows that one. Picking the cheaper copy is
//    the seller's money, so a store may want to offer a finish preference and
//    still not offer a one-tap "cheapest of everything".
//  * Changing a control does not re-pick anything. The shopper presses Apply. A
//    stray tap on a tablet must never silently reprice a cart.
//  * Apply re-reads live stock first. The copy chosen here is the copy checkout
//    reserves, and a kiosk cart survives reloads, so deciding against the numbers
//    captured when the card was added could put the cart on a copy that has since
//    sold. Everything the re-pick could not do is reported back rather than
//    quietly dropped.
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useCart } from '../contexts/CartContext';
import { useKioskTheme } from '../contexts/KioskThemeContext';
import { fetchCartVariants } from '../api/products';
import { clampPreferencesToAllowed, isDefaultPreferences } from '../utils/cartOptimizer';

/** Conditions offered as a floor. DM is the bottom of the ladder, so a DM floor is just "Any". */
const CONDITION_FLOORS = ['NM', 'LP', 'MP', 'HP'];

const CONDITION_NAMES = {
    NM: 'Near Mint',
    LP: 'Lightly Played',
    MP: 'Moderately Played',
    HP: 'Heavily Played',
};

const toggleSx = {
    '& .MuiToggleButton-root': {
        borderRadius: 2,
        textTransform: 'none',
        fontWeight: 600,
        minHeight: 44,
        px: 2,
    },
};

/**
 * Turn the optimiser summary into sentences a shopper can act on. Every branch
 * names a card count, because "some items could not be changed" tells them
 * nothing about what to look at.
 */
export function describeSummary(summary, formatPrice) {
    if (!summary) return [];
    const lines = [];
    const cards = n => `${n} ${n === 1 ? 'card' : 'cards'}`;

    // TKT-2026-105480
    if (summary.orderLimit) {
        return ['Those changes would take your order past this store\'s kiosk order limit, so your cart was left as it was.'];
    }

    if (summary.switched > 0) {
        lines.push(`Switched ${cards(summary.switched)} to a different copy.`);
        const delta = summary.priceDelta || 0;
        if (delta < 0) {
            lines.push(`Your total is ${formatPrice(Math.abs(delta))} lower.`);
        } else if (delta > 0) {
            lines.push(`Your total is ${formatPrice(delta)} higher.`);
        }
    }

    if (summary.merged > 0) {
        lines.push(`${summary.merged === 1 ? 'One line' : `${summary.merged} lines`} ended up on a copy already in your cart, so they were combined.`);
    }

    const noCondition = (summary.blocked || []).filter(b => b.reason === 'condition');
    if (noCondition.length) {
        lines.push(`${cards(noCondition.length)} had no copy that meets your minimum condition, so they were left as they were.`);
    }

    const gone = (summary.blocked || []).filter(b => b.reason === 'unavailable');
    if (gone.length) {
        lines.push(`${cards(gone.length)} are no longer in stock in store. Please remove them before checking out.`);
    }

    if ((summary.reduced || []).length) {
        lines.push(`${cards(summary.reduced.length)} were reduced to the quantity still on the shelf.`);
    }

    if (!lines.length) lines.push('Your cart already matches those preferences.');
    return lines;
}

export default function CartOptimizerPanel({ storeName, settings = null }) {
    const { formatMoney } = useKioskTheme();
    const {
        sellCartItems,
        cartPreferences,
        setCartPreference,
        resetCartPreferences,
        applyCartOptimization,
    } = useCart();

    const [applying, setApplying] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const allowCost = settings?.enabled === true && settings?.allowCost === true;
    const allowFinish = settings?.enabled === true && settings?.allowFinish === true;
    const allowCondition = settings?.enabled === true && settings?.allowCondition === true;
    const anyAllowed = allowCost || allowFinish || allowCondition;

    // Anything the store has since withheld stops steering the picks, even if the
    // shopper set it while it was still on offer.
    const prefs = useMemo(
        () => clampPreferencesToAllowed(cartPreferences, { allowCost, allowFinish, allowCondition }),
        [cartPreferences, allowCost, allowFinish, allowCondition],
    );
    const nothingChosen = isDefaultPreferences(prefs);

    const handleChange = useCallback((key) => (_event, value) => {
        if (value == null) return; // exclusive groups fire null when the active button is re-tapped
        setResult(null);
        setError('');
        setCartPreference(key, value);
    }, [setCartPreference]);

    const handleReset = useCallback(() => {
        setResult(null);
        setError('');
        resetCartPreferences();
    }, [resetCartPreferences]);

    const handleApply = useCallback(async () => {
        const productIds = [...new Set(sellCartItems.map(item => item.productId).filter(id => id != null))];
        if (!productIds.length) return;

        setApplying(true);
        setError('');
        setResult(null);
        try {
            const { data } = await fetchCartVariants(storeName, productIds);
            setResult(applyCartOptimization(data?.variants || {}, prefs));
        } catch (err) {
            // Never guess at stock. If we could not read it, the cart is left
            // exactly as it was and the shopper is told to try again.
            setError(
                err?.response?.status === 403
                    ? 'Cart preferences are not available at this kiosk.'
                    : 'We could not check what is on the shelf just now. Please try again.',
            );
        } finally {
            setApplying(false);
        }
    }, [sellCartItems, storeName, applyCartOptimization, prefs]);

    if (!anyAllowed || sellCartItems.length === 0) return null;

    return (
        <Box
            sx={(theme) => ({
                mb: 3,
                p: { xs: 2, md: 2.5 },
                borderRadius: '12px',
                background: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.06 : 0.14),
                border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.16 : 0.28)}`,
            })}
        >
            <Stack spacing={2}>
                <Box>
                    <Typography variant='subtitle1' fontWeight={700}>
                        Cart preferences
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        When we have more than one copy of a card, choose which one you get.
                    </Typography>
                </Box>

                {allowCost && (
                    <Box>
                        <Typography variant='caption' color='text.secondary' sx={{ mb: 0.5, display: 'block' }}>
                            Price
                        </Typography>
                        <ToggleButtonGroup
                            value={prefs.price}
                            exclusive
                            size='small'
                            onChange={handleChange('price')}
                            sx={toggleSx}
                            aria-label='Price preference'
                        >
                            <ToggleButton value='default'>Best condition</ToggleButton>
                            <ToggleButton value='cheapest'>Lowest price</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}

                {allowFinish && (
                    <Box>
                        <Typography variant='caption' color='text.secondary' sx={{ mb: 0.5, display: 'block' }}>
                            Finish
                        </Typography>
                        <ToggleButtonGroup
                            value={prefs.finish}
                            exclusive
                            size='small'
                            onChange={handleChange('finish')}
                            sx={toggleSx}
                            aria-label='Finish preference'
                        >
                            <ToggleButton value='any'>No preference</ToggleButton>
                            <ToggleButton value='foil'>Prefer foil</ToggleButton>
                            <ToggleButton value='nonfoil'>Prefer non foil</ToggleButton>
                        </ToggleButtonGroup>
                        <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
                            If we do not have the finish you prefer, you still get the card.
                        </Typography>
                    </Box>
                )}

                {allowCondition && (
                    <Box>
                        <Typography variant='caption' color='text.secondary' sx={{ mb: 0.5, display: 'block' }}>
                            Minimum condition
                        </Typography>
                        <ToggleButtonGroup
                            value={prefs.minCondition}
                            exclusive
                            size='small'
                            onChange={handleChange('minCondition')}
                            sx={toggleSx}
                            aria-label='Minimum condition'
                        >
                            <ToggleButton value='any'>Any</ToggleButton>
                            {CONDITION_FLOORS.map(cond => (
                                <ToggleButton key={cond} value={cond} title={CONDITION_NAMES[cond]}>
                                    {cond} or better
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Button
                        variant='contained'
                        onClick={handleApply}
                        disabled={applying || nothingChosen}
                        startIcon={applying ? <CircularProgress size={16} color='inherit' /> : null}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 3, minHeight: 44 }}
                    >
                        {applying ? 'Checking the shelf...' : 'Apply to my cart'}
                    </Button>
                    {!nothingChosen && (
                        <Button
                            variant='outlined'
                            onClick={handleReset}
                            disabled={applying}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 3, minHeight: 44 }}
                        >
                            Clear preferences
                        </Button>
                    )}
                </Stack>

                {error && <Alert severity='warning'>{error}</Alert>}

                {result && !error && (
                    <Alert severity={(result.blocked || []).length || result.orderLimit ? 'info' : 'success'}>
                        <Stack spacing={0.5}>
                            {describeSummary(result, formatMoney).map(text => (
                                <Typography key={text} variant='body2'>{text}</Typography>
                            ))}
                        </Stack>
                    </Alert>
                )}
            </Stack>
        </Box>
    );
}

CartOptimizerPanel.propTypes = {
    storeName: PropTypes.string.isRequired,
    /** Store permissions from kioskConfig.cartOptimization. Absent or disabled renders nothing. */
    settings: PropTypes.shape({
        enabled: PropTypes.bool,
        allowCost: PropTypes.bool,
        allowFinish: PropTypes.bool,
        allowCondition: PropTypes.bool,
    }),
};
